import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Formik, Form } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAlert } from '../../../../components/alertProvider';
import { RequiredLabel } from '../../../../utils/commonUtils';
import { useAppQuery } from '../../../../services/appQuery';
import { getRequest, postRequest } from '../../../../services/httpService';
import { AdvanceConfigLookups, AdvancePaymentPayload, advanceInitialValues, validationSchema, getbillingConfigURL, AdvanceRequestItem, postMakeAdvancePaymentURL, MakePayment, PaymentDetailPayload, } from '../advance.helper';
import { usePatientContext } from '../../../../components/patinetProvider';
import HeaderProfile from '../../../../components/header_Profile/header_Profile.index';
import useCurrency from '../../../../components/CurrencyProvider';
import { ReactComponent as ViewIcon } from '../../../../assets/SVG/viewIconSvg.svg';
import SummaryCard from '../Package_Summary_Card';
import { CollectPaymentModal, PaymentDetails, PaymentFormValues, PaymentHistoryItem } from '../../collectPayment';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

const CollectAdvance: React.FC = () => {
  const [latestFormValues, setLatestFormValues] = useState<AdvancePaymentPayload | null>(null);
  const queryClient = useQueryClient();
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const [paymentDetailsForModal, setPaymentDetailsForModal] = useState<PaymentDetails | null>(null);
  const { t : tBilling } = useTranslation('billing_config');
  
  const navigate = useNavigate();
  const { currency } = useCurrency();

  const location = useLocation();
  const { addAlert } = useAlert();
  const { patientId, MainpatientDetails } = usePatientContext();

  // Get the state passed from navigation (AdvanceRequestItem)
  const summary: AdvanceRequestItem | null = location.state && typeof location.state === 'object' && 'summary' in location.state
    ? (location.state as { summary: AdvanceRequestItem }).summary
    : null;

  const { data: billingConfigAdvance } = useAppQuery<AdvanceConfigLookups>(
    ['billingConfigAdvance'],
    () => getRequest<AdvanceConfigLookups>(getbillingConfigURL()),
    { staleTime: 5 * 60 * 1000 }
  );

  const { mutate: submitPayment, isPending: isSubmittingPayment } = useMutation({
    mutationFn: (payload: MakePayment) => postRequest(postMakeAdvancePaymentURL(), payload),
    onSuccess: async () => {
      addAlert(tBilling('Advance.messages.payment_saved_success', { defaultValue: 'Payment saved successfully.' }), 'success');
      setCollectPaymentOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['AdvanceRequestList'] });
      navigate(`/admin/billing/advance/patient-advance/${patientId}?advance-request`);
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message || tBilling('Advance.messages.payment_save_failed', { defaultValue: 'Failed to save payment.' });
      addAlert(message, 'error');
    },
  });

  const handleOpenCollectPayment = (netAmount: number, patientName: string, patientIdValue: string) => {    
    setPaymentDetailsForModal({
      date: new Date().toLocaleDateString('en-GB'),
      patientName: patientName || 'Patient',
      patientId: patientIdValue || '',
      currency: currency,
      netAmount: netAmount,
    });
    setCollectPaymentOpen(true);
  };

  const handlePaymentSave = (data: PaymentFormValues, history: PaymentHistoryItem[] = []) => {
    if (!latestFormValues) {
      addAlert(tBilling('Advance.messages.missing_advance_form_values', { defaultValue: 'Missing advance form values. Please try again.' }), 'error');
      return;
    }
    if (!history.length) {
      addAlert(tBilling('Advance.messages.missing_payment_history', { defaultValue: 'Missing payment history. Please try again.' }), 'error');
      return;
    }

    const paymentCurrency = paymentDetailsForModal?.currency ?? currency;
    const isoOrNull = (d?: string) => (d ? new Date(d).toISOString() : new Date().toISOString());

    const payment_details: PaymentDetailPayload[] = history.map((item) => {
      const base: PaymentDetailPayload = {
        payment_mode_id: item.payment_mode_id ?? 0,
        paid_amount: String(item.amount ?? 0),
        currency: paymentCurrency,
        number: null,
        date: isoOrNull(item.date),
        bank_id: null,
        account_name: null,
        branch_name: null,
        ifsc_code: null,
        remarks: item.notes ?? null,
        cheque_number: null,
        cheque_date: null,
      };

      // Map numbers/cheque specifics based on method
      if (item.method?.toLowerCase().includes('bank') || item.method?.toLowerCase().includes('online') || item.method?.toLowerCase().includes('card') || item.method?.toLowerCase().includes('upi')) {
        base.number = item.transactionNumber ?? item.cardTransactionNumber ?? item.transactionNumber ?? null;
      }

      if (item.method?.toLowerCase().includes('cheque')) {
        base.cheque_number = item.chequeNumber ?? null;
        base.cheque_date = item.chequeDate ? new Date(item.chequeDate).toISOString() : null;
      }

      return base;
    });

    const totalPaid = payment_details.reduce((sum, p) => sum + (Number(p.paid_amount) || 0), 0);

    // Use display name for advance_against from config when available
    const againstName = latestFormValues.advance_against
      ? (billingConfigAdvance?.ADVANCE_AGAINST || []).find((opt) => opt.code === latestFormValues.advance_against)?.name || latestFormValues.advance_against
      : '';

    const payload: MakePayment = {
      invoice_id: null,
      patient_id: Number(patientId),
      clinic_id: MainpatientDetails?.clinic ?? 0,
      payment_against_type: 'ADVANCE',
      advance_data: {
        ...latestFormValues,
        advance_against: againstName,
        amount: totalPaid > 0 ? totalPaid : latestFormValues.amount,
      },
      remarks: latestFormValues.remark ?? '',
      payment_details,
    };

    submitPayment(payload);
  };
  return (

    <>
      <div>
        <HeaderProfile /> 
      </div>
      <Box p={3} data-testid="collect-advance-container">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => navigate(`/admin/billing/advance/patient-advance/${patientId}?advance-request`)}>
              <KeyboardBackspaceIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{tBilling('Advance.collect_advances')}</Typography>
          </Stack>
          <Stack direction="row" sx={{cursor: 'pointer'}} alignItems="center"  onClick={() => navigate(`../advance-logs/${patientId}`)} spacing={1}>

            <ViewIcon/>
            <Typography variant="body2" color="text.secondary" sx={{  fontWeight: 600,  color:'#5A8AEA' }}>
              {tBilling('Advance.view_advance_logs')}
            </Typography>
          </Stack>
        </Stack>

        <SummaryCard summary={summary} />

        <Paper sx={{mt:3, borderRadius: 2 }} elevation={0} data-testid="collect-advance-form">
          <Formik
            initialValues={{
              ...advanceInitialValues,
              advance_against:'PACKAGE_SERVICE',
              patient_package_id: summary?.package_id ?? undefined,
              appointment_visit: Number(summary?.visit_id ?? 0),
              advance_type: 'PACKAGE',
              balance_amount: Math.max(
                (summary?.package_net_amount ?? 0) - (summary?.package_paid_amount ?? 0),
                0,
              ),

            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              if(!values.appointment_visit){
                addAlert(tBilling('Advance.messages.no_visit_available_for_today', { defaultValue: 'No visit available for today to collect advance.' }), 'error');
                return;
              }
              setLatestFormValues({ ...values, appointment_visit: Number(values.appointment_visit) } as AdvancePaymentPayload);
              handleOpenCollectPayment(values.amount ?? 0, `${MainpatientDetails?.first_name} ${MainpatientDetails?.last_name}`, String(patientId));
            }}
          >
            {({ values, handleChange, handleBlur, touched, errors }) => (
              <Form>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      fullWidth
                      label={RequiredLabel(tBilling('Advance.advance_against'), true)}
                      name="advance_against"
                      size="small"
                      sx={{
                        backgroundColor: '#fafafa',
                        opacity: 0.9,
                        transition: 'background-color 0.3s ease, opacity 0.3s ease',
                      }}
                      slotProps={{ inputLabel: { shrink: true }, input: { readOnly: true } }}
                      
                      value={values.advance_against}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.advance_against && Boolean(errors.advance_against)}
                      helperText={touched.advance_against && (errors.advance_against as string)}
                    >
                      {billingConfigAdvance?.ADVANCE_AGAINST.map((opt) => (
                        <MenuItem key={opt.code} value={opt.code}>
                          {opt.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={RequiredLabel(tBilling('Advance.date'), true)}
                      type="date"
                      size="small"
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      slotProps={{ inputLabel: { shrink: true }, input: { readOnly: true } }}
                      sx={{
                        backgroundColor: '#fafafa',
                        opacity: 0.9,
                        transition: 'background-color 0.3s ease, opacity 0.3s ease',
                      }}
                      error={touched.date && Boolean(errors.date)}
                      helperText={touched.date && (errors.date as string)} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={RequiredLabel(tBilling('Advance.amount'), true)}
                      name="amount"
                      type="number"
                      size="small"
                      value={values.amount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.amount && Boolean(errors.amount)}
                      helperText={touched.amount && (errors.amount as string)} />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={3}
                      label={RequiredLabel(tBilling('Advance.remark'), false)}
                      placeholder={tBilling('Advance.type_here')}
                      name="remark"
                      size="small"
                      value={values.remark}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.remark && Boolean(errors.remark)}
                      helperText={touched.remark && (errors.remark as string)} />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }} display="flex" justifyContent="flex-end" gap={2} mt={1}>
                    <Button variant="outlined" sx={{ borderRadius: 3, minWidth: 140 }} type="button" onClick={() => navigate(`/admin/billing/advance/patient-advance/${patientId}?advance-request`)}>
                      {tBilling('package_details.cancel')}
                    </Button>
                    <Button variant="contained" sx={{ borderRadius: 3, minWidth: 180 }} type="submit" disabled={isSubmittingPayment}>
                      {tBilling('Advance.collect_payment')}
                    </Button>
                  </Grid2>
                </Grid2>
                {paymentDetailsForModal && (
                  <CollectPaymentModal
                    paymentType="ADVANCE"
                    open={collectPaymentOpen}
                    onClose={() => setCollectPaymentOpen(false)}
                    onSave={handlePaymentSave}
                    paymentDetails={paymentDetailsForModal}
                    existingPaymentHistory={[]}
                    walletAdvances={[]}
                    paymentModes={billingConfigAdvance?.modeofpayment || []}
                    hidePaymentModeToggle={true}
                    isSubmitting={isSubmittingPayment}
                  />
                )}
              </Form>
            )}
          </Formik>
        </Paper>
      </Box></>
  );
};

export default CollectAdvance;

